import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Form, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { zynity_api } from '../../../config/external-routes';
import { Loading } from '../loading/Loading';

export type DocumentLibraryPickerItem = {
    id: number;
    type: 'link' | 'external_doc' | 'internal_doc';
    source_provider: string;
    title: string;
    description: string | null;
    display_name: string | null;
    external_url: string | null;
    storage_path: string | null;
    mime_type: string | null;
    download_url: string | null;
    author: { id: number; name: string } | null;
};

type DocumentLibraryPickerPagination = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
};

export const DocumentLibraryPickerModal = ({
    show,
    onHide,
    onSelectItem,
    selectedItemIds = [],
}: {
    show: boolean;
    onHide: () => void;
    onSelectItem: (item: DocumentLibraryPickerItem) => void;
    selectedItemIds?: number[];
}) => {
    const { organization_id, team_id } = useParams();
    const [items, setItems] = useState<DocumentLibraryPickerItem[]>([]);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState<DocumentLibraryPickerPagination>({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: null,
        to: null,
    });

    const fetchItems = async (page = pagination.current_page, perPage = pagination.per_page) => {
        if (!show || !organization_id || !team_id) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(
                zynity_api.organization_team_document_library
                    .replace('{organization_id}', organization_id)
                    .replace('{team_id}', team_id),
                {
                    params: {
                        q: query.trim() === '' ? undefined : query.trim(),
                        limit: perPage,
                        page,
                    },
                }
            );

            setItems(response.data.data ?? []);
            setPagination({
                current_page: response.data.current_page ?? page,
                last_page: response.data.last_page ?? 1,
                per_page: response.data.per_page ?? perPage,
                total: response.data.total ?? 0,
                from: response.data.from ?? null,
                to: response.data.to ?? null,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!show) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            fetchItems(1, pagination.per_page);
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [show, query, organization_id, team_id]);

    useEffect(() => {
        if (show) {
            return;
        }

        setItems([]);
        setPagination({
            current_page: 1,
            last_page: 1,
            per_page: 10,
            total: 0,
            from: null,
            to: null,
        });
    }, [show]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            className="nested-modal-dialog"
            backdropClassName="nested-modal-backdrop"
        >
            <Modal.Header closeButton>
                <Modal.Title>Choose from Library</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Control
                        type="text"
                        placeholder="Search team documents..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </Form.Group>

                <div className="d-flex justify-content-between align-items-center small text-muted mb-3">
                    <span>
                        {loading
                            ? 'Loading...'
                            : pagination.total === 0
                                ? '0 results'
                                : `Showing ${pagination.from}-${pagination.to} of ${pagination.total}`}
                    </span>
                    <Form.Select
                        style={{ width: '140px' }}
                        size="sm"
                        value={pagination.per_page}
                        onChange={(e) => fetchItems(1, parseInt(e.target.value, 10))}
                    >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={30}>30 per page</option>
                    </Form.Select>
                </div>

                {loading ? (
                    <Loading />
                ) : items.length === 0 ? (
                    <div className="text-muted">No documents found.</div>
                ) : (
                    <div className="d-flex flex-column gap-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                        {items.map((item) => {
                            const isSelected = selectedItemIds.includes(item.id);
                            return (
                                <div key={item.id} className="border rounded p-3 d-flex justify-content-between align-items-start gap-3">
                                    <div>
                                        <div className="fw-bold">{item.title}</div>
                                        <div className="small text-muted">
                                            {item.type === 'internal_doc' ? 'Internal document' : item.type === 'external_doc' ? 'External document' : 'Link'}
                                            {item.author?.name ? ` • ${item.author.name}` : ''}
                                        </div>
                                        {item.description && <div className="small mt-1">{item.description}</div>}
                                    </div>
                                    <Button
                                        className="btn-teal"
                                        size="sm"
                                        disabled={isSelected}
                                        onClick={() => onSelectItem(item)}
                                    >
                                        {isSelected ? 'Selected' : 'Choose'}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <div className="small text-muted">
                    Page {pagination.current_page} of {Math.max(pagination.last_page, 1)}
                </div>
                <div className="d-flex gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={loading || pagination.current_page <= 1}
                        onClick={() => fetchItems(pagination.current_page - 1, pagination.per_page)}
                    >
                        Previous
                    </Button>
                    <Button
                        className="btn-teal"
                        size="sm"
                        disabled={loading || pagination.current_page >= pagination.last_page}
                        onClick={() => fetchItems(pagination.current_page + 1, pagination.per_page)}
                    >
                        Next
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};
